import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Mail, Loader2, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { inputCls } from "@/components/hub/Common";

export const useRateAlerts = () => {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/admin/rate-alerts").then((r) => setRows(r.data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  return [rows, load];
};

const TPL = {
  fr: {
    subject: "Tu crées beaucoup sur CLIP-EX !",
    body: "Salut {name} !\n\nOn a remarqué que tu atteins souvent tes limites d'imports ou de lives. Bravo pour ton activité !\n\nAvec un forfait de crédits, tes limites par heure sont doublées.{bonus}\n\nUne question ? Réponds simplement à ce courriel.\n\nL'équipe CLIP-EX",
    bonus: " Et en ce moment, tu as un bonus de crédits sur ton prochain achat.",
  },
  en: {
    subject: "You create a lot on CLIP-EX!",
    body: "Hey {name}!\n\nWe noticed you often reach your import or live limits. Great job staying active!\n\nWith a credit plan, your hourly limits are doubled.{bonus}\n\nAny question? Just reply to this email.\n\nThe CLIP-EX team",
    bonus: " And right now you have a credit bonus on your next purchase.",
  },
};

const ContactDialog = ({ r, onClose, onSent }) => {
  const { t } = useI18n();
  const tpl = TPL[r.lang === "en" ? "en" : "fr"];
  const [subject, setSubject] = useState(tpl.subject);
  const [message, setMessage] = useState(tpl.body.replace("{name}", r.name || "").replace("{bonus}", r.has_offer ? tpl.bonus : ""));
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/rate-alerts/${r.user_id}/email`, { subject, message });
      toast.success(t("adm.contact_sent").replace("{e}", r.email));
      onSent();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0b0e1a] border-white/10 max-w-lg" data-testid="contact-dialog">
        <DialogHeader><DialogTitle className="text-white">{t("adm.contact_title").replace("{e}", r.email)}</DialogTitle></DialogHeader>
        <input className={inputCls} value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={150} data-testid="contact-subject" />
        <textarea className={`${inputCls} min-h-[180px]`} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={5000} data-testid="contact-message" />
        <div className="flex justify-end">
          <button onClick={send} disabled={busy || subject.trim().length < 3 || message.trim().length < 10} data-testid="contact-send"
            className="cx-btn rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}{t("adm.contact_send")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const AlertRow = ({ r, onContact }) => {
  const { t } = useI18n();
  return (
    <div className="cx-card rounded-2xl p-5 flex flex-wrap items-center gap-4" data-testid={`admin-alert-${r.user_id}`}>
      <ShieldAlert className="w-5 h-5 text-rose-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-white font-display truncate">{r.name || r.email}</div>
        <div className="text-xs text-slate-500 truncate">{r.email}</div>
      </div>
      <div className="flex flex-wrap items-center gap-4 text-xs">
        <span className="text-rose-300" data-testid={`admin-alert-imports-${r.user_id}`}>{t("adm.alerts_imports").replace("{n}", r.imports)}</span>
        <span className="text-amber-300" data-testid={`admin-alert-lives-${r.user_id}`}>{t("adm.alerts_lives").replace("{n}", r.lives)}</span>
        <span className="text-slate-500">{new Date(r.last_at).toLocaleString()}</span>
        {r.contacted_at && <span className="text-emerald-300 inline-flex items-center gap-1" data-testid={`admin-alert-contacted-${r.user_id}`}><CheckCircle2 className="w-3.5 h-3.5" />{t("adm.contacted").replace("{d}", new Date(r.contacted_at).toLocaleDateString())}</span>}
        <button onClick={() => onContact(r)} data-testid={`admin-alert-contact-${r.user_id}`} className="rounded-full px-4 py-2 border border-white/15 text-slate-200 hover:border-[#3ad7ff] inline-flex items-center gap-1.5">
          <Mail className="w-3.5 h-3.5" />{t("adm.contact")}
        </button>
      </div>
    </div>
  );
};

export const AlertsTab = () => {
  const { t } = useI18n();
  const [rows, load] = useRateAlerts();
  const [target, setTarget] = useState(null);
  if (!rows) return null;
  return (
    <div className="space-y-4" data-testid="admin-alerts">
      <p className="text-sm text-slate-400">{t("adm.alerts_sub")}</p>
      {!rows.length && <div className="cx-card rounded-2xl p-6 text-slate-400 text-sm" data-testid="admin-alerts-empty">{t("adm.alerts_empty")}</div>}
      {rows.map((r) => <AlertRow key={r.user_id} r={r} onContact={setTarget} />)}
      {target && <ContactDialog r={target} onClose={() => setTarget(null)} onSent={() => { setTarget(null); load(); }} />}
    </div>
  );
};
